import React, { useEffect, useState } from 'react';
import { getDefaultUploadState } from './data';
import { SingleLocation, UploadedDataForTable, UploadState } from './types';
import {
  CREATE_DRAFT_ORDER_API,
  FETCH_APP_ORDER_API,
  FETCH_CSV_UPLOAD_STATUS_API,
  FETCH_CSV_UPLOAD_URL_API,
  FETCH_DOWNLOAD_CATALOGUE_API,
  FETCH_DOWNLOAD_CATALOGUE_STATUS_API,
  FETCH_LOCATIONS_API,
  rowsPerPage,
} from '../../features/csvUpload/constants';
import {
  errorKeyMap,
  errorVariables,
  maxPossibleItemQty,
  quantityErrors,
  statusRequestInterval,
} from './constants';
import { authenticatedFetch } from '../../common/api';
import {
  closestMultiples,
  createCart,
  getColumnHeadings,
  getColumnNameByIdentifier,
  getCreateOrderUrl,
  getErrorSummaryVariable,
  getProductNoteColumnIDs,
  getProgressValueByStatus,
  getSafeNumber,
  getValidUrl,
  isValueMultipleOfIncrement,
  orderSummaryDataTransformation,
  updateWindowUpro,
} from './utils';
import { downloadCSVFile, getParsedCsvFile, uploadCsvFile } from './fileUtils';
import { getURLWithBasicParams } from './request';
import { CSVUpload as CSVUploadComponent } from 'mrca-components';
import { getCountry, getShop } from '../../common/pageData';
import { addAttributesToCart, getInlineError } from '../utils';
import { ShopExcludedForClearingCart } from '../../features/constants';
import { useQuickSearchState } from './utils/useQuickSearchState';

const CSVUpload = ({ defaultOfferData }) => {
  const [offerData, setOfferData] = useState(defaultOfferData);
  const [locationList, setLocationList] = useState<SingleLocation[]>([]);
  const [locationOptions, setLocationOptions] = useState<SingleLocation[]>([]);
  const [locationSearchValue, setLocationSearchValue] = useState('');
  const [selectedLocation, setSelectedLocation] = useState({
    id: '',
    name: '',
    countryCode: '',
  });
  const [progressValue, setProgressValue] = useState(0);

  const defaultUploadState = getDefaultUploadState(
    offerData.theme.uploadButtonText,
  );
  const [uploadState, setUploadState] =
    useState<UploadState>(defaultUploadState);
  const [downloadState, setDownloadState] = useState({
    downloadID: 0,
    downloadedFileData: {},
    downloadBtnLoading: false,
    downloadCatalogueURL: '',
    downloadCatalogueText: offerData.theme.downloadButtonText,
    isRequestForDownloadStatus: false,
    isHideDownloadButton: !offerData.isLatestProductCatalogueDownloadEnabled,
  });
  const [errorList, setErrorList] = useState<string[]>([]);
  const [isShowOrderTable, setIsShowOrderTable] = useState(false);
  const [activeTab, setActiveTab] = useState('by_download_upload');

  const [createOrderState, setCreateOrderState] = useState({
    createOrderButtonText: 'Create Order',
    createOrderUrl: '',
    createOrderLoading: false,
    mrOrderID: '',
    isCreateDraftOrder: false,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(
    uploadState.uploadedDataForTable?.length / rowsPerPage,
  );
  const [showDraftOrderURL, setShowDraftOrderURL] = useState(false);
  const [isInHouseClick, setIsInHouseClick] = useState(false);

  const getGlobalErrorMessage = (errorKey: string) => errorKeyMap[errorKey];
  const getErrorMessageFromTheme = (errorKey: string) =>
    offerData.theme[errorKey];

  const {
    isGoToCheckout,
    isUploadedItemsPreviewEnabled,
    isShowAvailableStock,
    watchTutorialLink,
    isShowWatchTutorial,
    isShowBarcode,
    columns,
    csvUploadFormat,
    isAvailableStockEnabledForQuickSearch,
  } = offerData;

  const incrementToken = getColumnNameByIdentifier('increment', columns);
  const minQtyToken = getColumnNameByIdentifier('minimum_quantity', columns);
  const maxQtyToken = getColumnNameByIdentifier('maximum_quantity', columns);
  const availableQtyToken = getColumnNameByIdentifier(
    'available_stock',
    columns,
  );
  const qtyToken = getColumnNameByIdentifier('quantity', columns);
  const variantIdToken = getColumnNameByIdentifier('variant_id', columns);
  const variantSkuToken = getColumnNameByIdentifier('variant_sku', columns);
  const barcodeToken = getColumnNameByIdentifier('barcode', columns);

  const columnsTokens = {
    increment: incrementToken,
    minimum_quantity: minQtyToken,
    maximum_quantity: maxQtyToken,
    available_stock: availableQtyToken,
    quantity: qtyToken,
    variant_id: variantIdToken,
    variant_sku: variantSkuToken,
    barcode: barcodeToken,
  };


  // quick search states
  const {
    searchedValue,
    setSearchedValue,
    searchedProducts,
    setSearchedProducts,
    quickSearchSelectedProduct,
    setQuickSearchSelectedProduct,
    quickSearchedTableData,
    setQuickSearchedTableData,
    orderUrl,
    createOrderButtonLoading,
    setCreateOrderButtonLoading,
    searchedProductsLoading,

    // utilities
    fetchSearchedProducts,
    onQuickSearchProductAdd,
    onQuickSearchProductChange,
    onClickCreateOrder,
    onQuickSearchProductChangeForSearchRow,
  } = useQuickSearchState(offerData, selectedLocation, columns, isGoToCheckout);

  const updatedWatchTutorialLink = getValidUrl(watchTutorialLink);

  

  const getIncrementErrorGroups = (data: UploadedDataForTable[]) =>
    data.reduce((acc, obj) => {
      const key = obj[incrementToken];
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(obj);
      return acc;
    }, {});

  // getting the updated error list based on the data
  const getUpdatedErrorList = (data: UploadedDataForTable[]) => {
    const errorArr: string[] = [];
    errorVariables.forEach((errorVar) => {
      const rowsWithThisError = data.filter(
        (elem) => elem['Is Error'] === 'true' && elem.Error === errorVar,
      );
      if (rowsWithThisError.length > 0) {
        if (errorVar === 'errorIncrementQuantityText') {
          const incErrors = getIncrementErrorGroups(rowsWithThisError);
          Object.keys(incErrors).forEach((key) => {
            const productList = incErrors[key]?.map((elem, index) => {
              const firstKey = errorSummaryVar || tableHeadings[0];

              return index === 0 ? `${firstKey}: ${elem[firstKey]}` : `${elem[firstKey]}`;
            });
  
            const incrementErrorText = `${productList.join(", ")}: ${getIncrementQtyErrorText(Number(key))}`;
            errorArr.push(incrementErrorText);
          });
        } else {
          const productList = rowsWithThisError?.map((elem, index) => {
            const firstKey = errorSummaryVar || tableHeadings[0];
            return index === 0 ? `${firstKey}: ${elem[firstKey]}` : `${elem[firstKey]}`;
          });
          const errorText = `${productList.join(", ")}: ${offerData.theme[errorVar]}`;
          errorArr.push(errorText);
        }
      }
    });
    return errorArr;
  };

  // fetching the location list
  useEffect(() => {
    if (offerData && offerData.isEligible) {
      fetchingLocations();
      setDownloadState({
        ...downloadState,
        downloadCatalogueText: offerData.theme.downloadButtonText,
        isHideDownloadButton:
          !offerData.isLatestProductCatalogueDownloadEnabled,
      });
      setUploadState({
        ...uploadState,
        uploadCsvButtonText: offerData.theme.uploadButtonText,
      });

      // set active tab based on the offer data
      if (offerData.isBulkOrderingEnabled && offerData.isQuickSearchEnabled) setActiveTab('by_download_upload');
      else if (offerData.isBulkOrderingEnabled) setActiveTab('by_download_upload');
      else if (offerData.isQuickSearchEnabled) setActiveTab('by_search');

    }
  }, [JSON.stringify(offerData)]);

  // updating the location options based on search value
  useEffect(() => {
    if (locationSearchValue === '') return;
    const filteredOptions = locationList.filter((elem) =>
      elem.name.toLowerCase().includes(locationSearchValue.toLowerCase()),
    );
    setLocationOptions([...filteredOptions]);
  }, [locationSearchValue]);

  // requesting upload status at a set time interval
  useEffect(() => {
    if (uploadState.isRequestForUploadStatus === true) {
      const interval = setInterval(() => {
        fetchingCsvUploadStatus();
      }, statusRequestInterval);
      return () => clearInterval(interval);
    }
  }, [uploadState.isRequestForUploadStatus]);

  const fetchingCsvUploadStatus = async () => {
    const fetchStatusUrl = `${FETCH_CSV_UPLOAD_STATUS_API}?id=${uploadState.uploadID}&shopURL=${offerData.shopURL}`;
    const data = await authenticatedFetch({
      api: fetchStatusUrl,
      method: 'GET',
    });
    if (data) {
      // progress value will be updated based on the status
      setProgressValue(getProgressValueByStatus(data.status));

      if (data.status === 'completed' && data.errors) {
        const mappedErrors = data.errors.map(getGlobalErrorMessage);
        setErrorList(mappedErrors.map(getErrorMessageFromTheme));
        setUploadState({
          ...uploadState,
          isRequestForUploadStatus: false,
        });
      }

      if (data.status === 'completed' && !data.errors) {
        setCreateOrderState({
          ...createOrderState,
          mrOrderID: data.mrOrderID,
          isCreateDraftOrder: data.isCreateDraftOrder,
        });
        // here we download the file and get the data
        const fileData = await downloadCSVFile(data.downloadURL);

        // here we parse the csv file and update the state
        const parsedData = await getParsedCsvFile(fileData as File);
        const ttd = orderSummaryDataTransformation(columns, parsedData);
        

        setUploadState({
          ...uploadState,
          isRequestForUploadStatus: false,
          uploadPageUrl: '',
          uploadCsvButtonText: offerData.theme.uploadButtonText,
          uploadedDataForTable: [...ttd as UploadedDataForTable[]],
          submitButtonLoading: false,
        });
      }
    }
  };

  useEffect(() => {
    // putting together all rows with errors to display above the table
    if (uploadState.uploadedDataForTable.length > 0) {
      let data: UploadedDataForTable[] = [];
      uploadState.uploadedDataForTable.forEach((elem) => {
        const updatedElem = { ...elem };
        if (elem['Is Error'] === 'true') {
          if (
            elem.Available == '' &&
            elem.Error === 'errorAvailableQuantityText'
          ) {
            updatedElem['Is Error'] = 'false';
            updatedElem.Error = '';
          }
        }
        data.push(updatedElem);
      });

      const errorArr = getUpdatedErrorList(data);
      setErrorList(errorArr);
      setUploadState({ ...uploadState, uploadedDataForTable: data });
    }

    // pagination edge case handling
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
    if (
      isUploadedItemsPreviewEnabled &&
      uploadState.uploadedDataForTable.length > 0
    ) {
      setIsShowOrderTable(true);
    }
  }, [uploadState.uploadedDataForTable.length]);

  // fetching the location list
  const fetchingLocations = async () => {
    // const locationData = {
    //   locations: [
    //     { id: '1', name: 'Location 1', countryCode: 'US' },
    //     // { id: '2', name: 'Location 2', countryCode: getCountry(selectedLocation?.countryCode) },
    //   ],
    // };
    const locationData = await authenticatedFetch({
      api: FETCH_LOCATIONS_API,
      useBasicParams: true,
    });
    if (locationData) {
      setLocationList(locationData.locations);
      setLocationOptions(locationData.locations);

      const customerDataElement = document.getElementById('mrca-customer-data');

      if (customerDataElement) {
        const currentLocationId = customerDataElement.getAttribute(
          'data-mrca-current-location-id',
        );

        // Use the currentLocationId for further logic
        if (currentLocationId) {
          // Example: set the selected location based on the current location ID
          const matchedLocation = locationData.locations.find((loc) =>
            loc.id.includes(currentLocationId),
          );
          if (matchedLocation) {
            setSelectedLocation(matchedLocation);
            return;
          }
        }
      } else {
        setSelectedLocation(locationData?.locations[0]);
      }
    }
  };

  const fetchDownloadStatus = async (downloadCatalogId, shopURL) => {
    // load catalog id in the api call
    const fetchStatusUrl = `${FETCH_DOWNLOAD_CATALOGUE_STATUS_API}?id=${downloadCatalogId}&shopURL=${shopURL}`;

    const data = await authenticatedFetch({
      api: fetchStatusUrl,
      method: 'GET',
    });

    if (data && data.status === 'completed') {
      // progress value will be updated based on the status
      setProgressValue(getProgressValueByStatus(data.status));

      if (data.status === 'completed') {
        // once we have the catalog url, update it and set loading values to false and rerender button
        setDownloadState({
          ...downloadState,
          downloadBtnLoading: false,
          downloadCatalogueURL: data.url,
          isRequestForDownloadStatus: false,
          downloadCatalogueText: offerData.theme.downloadButtonText,
        });

        setProgressValue(0);
      }
    }

    return data.status;
  };


  const onDownloadButtonClick = async (inHouseClick: boolean = false) => {
    // Update the inHouseClick state to prevent multiple status fetch calls. This is set to true when the download button is clicked via our UI, and false when triggered through the exposed function.
    setIsInHouseClick(inHouseClick);
    // set loading to true
    setDownloadState({
      ...downloadState,
      downloadBtnLoading: true,
    });

    // add location id, country code to api call
    const locationId = selectedLocation?.id;
    const countryCode = getCountry(selectedLocation?.countryCode);
    const downloadUrl = `${getURLWithBasicParams(FETCH_DOWNLOAD_CATALOGUE_API)}${locationId ? `&locationID=${locationId}` : ''}&countryCode=${countryCode}&extensionID=${offerData.extensionID}`;

    const data = await authenticatedFetch({
      api: downloadUrl,
      method: 'GET',
    });

    // if the res is ok, we'll load catalog id and call fetchDownloadStatus()
    if (data) {
      setDownloadState({
        ...downloadState,
        downloadID: Number(data.id),
        isRequestForDownloadStatus: true,
        downloadBtnLoading: true,
      });

      setProgressValue(25);

      updateWindowUpro({downloadCatalogID: Number(data.id)});
    }
  };

  let downloadStatusInterval;

  const FetchStatus = (downloadID, updateStatusCallback?) => {
      // Clear any existing interval to avoid multiple intervals
      if (downloadStatusInterval) {
          clearInterval(downloadStatusInterval);
      }

      downloadStatusInterval = setInterval(async () => {
          try {
              const status = await fetchDownloadStatus(downloadID, offerData.shopURL);
              
              if (updateStatusCallback) {
                  updateStatusCallback(status);
              }

              if (status === 'completed') {
                  clearInterval(downloadStatusInterval);
              }
          } catch (error) {
              console.error('Error fetching download status:', error);
              clearInterval(downloadStatusInterval); 
          }
      }, statusRequestInterval);
  };

  updateWindowUpro({getDowloadCatalogStatus: FetchStatus});

  useEffect(() => {
    if (
      downloadState.isRequestForDownloadStatus &&
      isInHouseClick
    ) {
      FetchStatus(downloadState.downloadID);
    }
    if (downloadState.downloadCatalogueURL) {
      window.location.href = downloadState.downloadCatalogueURL;
      setDownloadState({
        ...downloadState,
        downloadCatalogueURL: '',
      });
    }
  }, [
    downloadState.isRequestForDownloadStatus,
    downloadState.downloadCatalogueURL,
  ]);

  // table pagination handling
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // table pagination handling
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // setting the current data to display based on page number and total rows
  const currentData = uploadState.uploadedDataForTable;
  // const incrementToken = getColumnNameByIdentifier('increment', columns);
  // const minQtyToken = getColumnNameByIdentifier('minimum_quantity', columns);
  // const maxQtyToken = getColumnNameByIdentifier('maximum_quantity', columns);
  // const availableQtyToken = getColumnNameByIdentifier(
  //   'available_stock',
  //   columns,
  // );
  // const qtyToken = getColumnNameByIdentifier('quantity', columns);
  // const variantIdToken = getColumnNameByIdentifier('variant_id', columns);
  // const variantSkuToken = getColumnNameByIdentifier('variant_sku', columns);
  // const barcodeToken = getColumnNameByIdentifier('barcode', columns);


  const getIncrementQtyErrorText = (incrementValue: number) => {
    const updatedError = offerData.theme.errorIncrementQuantityText.replace(
      '{increment_qty}',
      String(incrementValue),
    );
    return updatedError;
  };

  // const isValueMultipleOfIncrement = (
  //   value: number,
  //   incrementValue: number,
  // ) => {
  //   return value % incrementValue === 0;
  // };

  // const closestMultiples = (increment: number, value: number) => {
  //   const lowerMultiple = value - (value % increment);
  //   const upperMultiple =
  //     lowerMultiple + increment * (value % increment === 0 ? 0 : 1);
  //   return [lowerMultiple, upperMultiple];
  // };

  // updating the state on quantity increment or decrement in the table

  /*
    Test Cases : 
      ✔ Exceeds Available Stock → Error: errorAvailableQuantityText
      ✔ Below Minimum Quantity → Error: errorMinOrderQuantityText
      ✔ Exceeds Max Allowed Quantity → Error: errorMaxOrderQuantityText
      ✔ Not multiple of increment → Error: errorIncrementQuantityText
      ✔ Rounds to Nearest value → Adjusts quantity based on increment rule
      x Max check →  Ensures cart quantity does not exceed the defined maximum when available stock is higher // TODO

      ✔ Clears Error on Valid Entry → Removes error when correct quantity is entered
      ✔ Clears Error on Zero Quantity → Resets error messages
  */
  const onTableRowQuantityChange = (index: number, value: number) => {
    if (uploadState?.uploadedDataForTable.length > 0) {
      // extracting values
      const tableData = structuredClone(uploadState.uploadedDataForTable);

      const maxQty = getSafeNumber(
        tableData[index],
        maxQtyToken,
        maxPossibleItemQty,
      );
      const availableStock = Math.max(
        0,
        getSafeNumber(tableData[index], availableQtyToken, maxPossibleItemQty),
      );
      const minQty = getSafeNumber(tableData[index], minQtyToken, 0);
      const increment = getSafeNumber(tableData[index], incrementToken, 1);
      const currentQty = getSafeNumber(tableData[index], qtyToken, 0);

      const adding = value > currentQty;
      const subtracting = value < currentQty;
      const sameValue = value === currentQty;
      // calculating the maximum quantity that can be added
      const maxQtyThatCanBeAdded = Math.min(maxQty, availableStock);
      const minQtyToBeAdded = Math.min(minQty, availableStock);

      const isCurrentValMultipleOfIncrement = isValueMultipleOfIncrement(
        currentQty,
        increment,
      );

      // handling value change based on increment
      let updatedValue = value;

      if (!sameValue) {
        const [lowerMultiple, upperMultiple] = closestMultiples(
          increment,
          isCurrentValMultipleOfIncrement ? value : currentQty,
        );
        const valueAfterSubtracting = Math.max(lowerMultiple, minQtyToBeAdded);
        // if the upper multiple is greater than the quantity that can be added, we can't increase the value
        const valueAfterAdding =
          upperMultiple > maxQtyThatCanBeAdded ? currentQty : upperMultiple;
        updatedValue = subtracting ? valueAfterSubtracting : valueAfterAdding;
      }

      if (updatedValue < minQtyToBeAdded) {
        const qtyToAddWhenLessThanMin = Math.min(
          minQtyToBeAdded,
          maxQtyThatCanBeAdded,
        );
        updatedValue = qtyToAddWhenLessThanMin;
      }

      if (updatedValue > maxQtyThatCanBeAdded) {
        updatedValue = maxQtyThatCanBeAdded;
      }

      const isAddedQuantityLessThanMin = updatedValue < minQty;
      const isAddedQuantityMoreThanMax = updatedValue > maxQty;
      const isAddedQuantityMoreThanAvailable = updatedValue > availableStock;

      // we will update the quantity only if it's within limits. refer to the if condition below
      if (
        updatedValue <= maxQtyThatCanBeAdded &&
        updatedValue >= minQtyToBeAdded
      ) {
        let elementWithUpdatedQuantity = {
          ...tableData[index],
          [qtyToken]: String(updatedValue),
        };
        // Handling the error within the element
        if (elementWithUpdatedQuantity['Is Error'] === 'true') {
          const { Error } = elementWithUpdatedQuantity;

          // Clear the error if it's a known quantity error or increment issue is resolved
          if (
            quantityErrors.includes(Error) ||
            (Error === 'errorIncrementQuantityText' &&
              isValueMultipleOfIncrement(updatedValue, increment))
          ) {
            elementWithUpdatedQuantity = {
              ...elementWithUpdatedQuantity,
              ['Is Error']: 'false',
              Error: '',
            };
          }
        }

        // error conditions in a structured manner
        const errorConditions = [
          {
            condition:
              elementWithUpdatedQuantity['Is Error'] === 'false' &&
              !isValueMultipleOfIncrement(updatedValue, increment),
            error: 'errorIncrementQuantityText',
          },
          {
            condition:
              elementWithUpdatedQuantity['Is Error'] === 'false' &&
              isAddedQuantityMoreThanAvailable,
            error: 'errorAvailableQuantityText',
          },
          {
            condition:
              elementWithUpdatedQuantity['Is Error'] === 'false' &&
              isAddedQuantityLessThanMin,
            error: 'errorMinOrderQuantityText',
          },
          {
            condition:
              elementWithUpdatedQuantity['Is Error'] === 'false' &&
              isAddedQuantityMoreThanMax,
            error: 'errorMaxOrderQuantityText',
          },
        ];

        // Apply the first matching error condition
        for (const { condition, error } of errorConditions) {
          if (condition) {
            elementWithUpdatedQuantity = {
              ...elementWithUpdatedQuantity,
              ['Is Error']: 'true',
              Error: error,
            };
            break;
          }
        }

        // If quantity is 0, clear any errors
        if (updatedValue === 0) {
          elementWithUpdatedQuantity = {
            ...elementWithUpdatedQuantity,
            ['Is Error']: 'false',
            Error: '',
          };
        }

        const updatedTableData = [
          ...tableData.slice(0, index),
          elementWithUpdatedQuantity,
          ...tableData.slice(index + 1),
        ];
        // Setting the data for table and error list
        setUploadState({
          ...uploadState,
          uploadedDataForTable: updatedTableData,
        });

        setErrorList(getUpdatedErrorList(updatedTableData));
      }
    }
  };

  // handling the row deletion in table
  const onTableRowDelete = (idx: number) => {
    const tableData = [...uploadState?.uploadedDataForTable];
    const idxToRemove = idx + (currentPage - 1) * rowsPerPage;
    const filteredTableData = tableData.filter(
      (_, index) => index !== idxToRemove,
    );
    setUploadState({ ...uploadState, uploadedDataForTable: filteredTableData });
    const updatedErrorArr = getUpdatedErrorList(filteredTableData);
    setErrorList(updatedErrorArr);
  };

  const isShowCSVUpload = offerData.isEligible;
  // we have to reset the data in case of file deletion
  const onDeleteFile = () => {
    setErrorList([]);
    setUploadState({ ...defaultUploadState });
    setCreateOrderState({
      createOrderButtonText: 'Create Order',
      createOrderUrl: '',
      createOrderLoading: false,
      mrOrderID: '',
      isCreateDraftOrder: false,
    });
  };

  const isShowCreateOrderBtn = currentData.length > 0 && errorList.length === 0;

  const onCreateOrderClick = async () => {
    setCreateOrderState({ ...createOrderState, createOrderLoading: true });

    const showLineItemProperties = getProductNoteColumnIDs(columns);

    const requestBody = {
      locationID: selectedLocation?.id || null,
      countryCode: getCountry(selectedLocation?.countryCode),
      mrOrderID: createOrderState.mrOrderID,
      source: 'bulk_order'
    };
    // we redirect the user to cart or checkout based on the extension setup where they can purchase the items
    const data = await authenticatedFetch({
      api: FETCH_APP_ORDER_API,
      method: 'POST',
      isRequestBodyPresent: true,
      requestBody,
      useBasicParams: true,
    });

    if (data) {
      let orderUrl = '';
      // if the button is clicked for the first time
      if (createOrderState.isCreateDraftOrder) {
        orderUrl = await createDraftOrder();
        setShowDraftOrderURL(true);
      } else {
        const shopUrl = getShop();
        orderUrl = getCreateOrderUrl(offerData.shopURL, uploadState.uploadedDataForTable, columnsTokens, createOrderState.mrOrderID, isGoToCheckout);

        // manually creating cart for large orders and orders with line item properties enabled and cart merge shops
        if (showLineItemProperties.length > 0 || orderUrl.length > 2000 || ShopExcludedForClearingCart.includes(shopUrl)) {
          const data = await createCart(
            uploadState.uploadedDataForTable,
            columnsTokens,
            columns,
          );
          await addAttributesToCart(createOrderState.mrOrderID);
          let redirectPath = '/cart';
          if (isGoToCheckout) {
            redirectPath = '/checkout';
          }
          const toastParam = data ? `?toast=${data}` : '';
          window.location.href = `${redirectPath}${toastParam}`;
        } else {
          // creating cart using order url for small orders
          window.location.href = orderUrl;
        }
      }

      setCreateOrderState({
        ...createOrderState,
        createOrderLoading: false,
        createOrderUrl: orderUrl,
      });
    }
  };

  // can be moved elsewhere
  

  const createDraftOrder = async () => {
    type lineItem = {
      variantID: number;
      quantity: number;
    };

    type createDraftOrderRequestBody = {
      mrOrderID: string;
      uploadID: number;
      lineItems: lineItem[];
    };

    const lineItems = uploadState.uploadedDataForTable
      .filter(
        (item) => Number(item[qtyToken]) > 0,
      ) /* Exclude items with Quantity <= 0 */
      .map((item) => {
        return {
          variantID: Number(item[variantIdToken]),
          quantity: Number(item[qtyToken]),
        };
      });

    const requestBody = {
      mrOrderID: createOrderState.mrOrderID,
      uploadID: uploadState.uploadID,
      lineItems: lineItems,
      source: 'bulk_order',
    } as createDraftOrderRequestBody;

    const data = await authenticatedFetch({
      api: CREATE_DRAFT_ORDER_API,
      method: 'POST',
      isRequestBodyPresent: true,
      requestBody,
      useBasicParams: true,
    });

    return data.orderURL;
  };

  const onFileInput = (file: File) => {
    setUploadState({
      ...uploadState,
      uploadFileName: file.name,
      fileToUpload: file,
    });
  };

  const onFileDelete = () => {
    setUploadState({ ...uploadState, uploadFileName: '', fileToUpload: null });
  };

  const onModalCancelButtonClick = () => {
    // ui.overlay.close('upload-modal');
  };

  const onFileSubmit = async () => {
    // enable loading as soon as the user clicks on submit and reset the progress value
    setErrorList([]);
    setUploadState({ ...uploadState, submitButtonLoading: true });
    setProgressValue(0);

    const requestBody = {
      extensionID: offerData.extensionID,
      locationID: selectedLocation?.id || null,
      countryCode: getCountry(selectedLocation?.countryCode),
      fileName: uploadState?.fileToUpload?.name,
    };
    // in the below request we get uploadID and `uploadURL` to which we will upload the file
    const fileUploadData = await authenticatedFetch({
      api: FETCH_CSV_UPLOAD_URL_API,
      method: 'POST',
      requestBody,
      isRequestBodyPresent: true,
      useBasicParams: true,
    });
    if (fileUploadData) {
      const { uploadURL, id } = fileUploadData;
      // below we upload the file to the url we got from the above request
      const res = await uploadCsvFile({
        url: uploadURL,
        contentType: uploadState?.fileToUpload?.type,
        file: uploadState.fileToUpload,
      });
      if (res && res.status === 200) {
        // when the request is successful we set the uploadID and start requesting for the status
        setUploadState({
          ...uploadState,
          isRequestForUploadStatus: true,
          uploadID: Number(id),
          uploadedDataForTable: [],
          fileToUpload: null,
          uploadError: '',
        });
        setErrorList([]);
        // ui.overlay.close('upload-modal');
      }
      if (res && res.status !== 200) {
        // error handling
        setUploadState({
          ...uploadState,
          isRequestForUploadStatus: false,
          uploadedDataForTable: [],
          fileToUpload: null,
          uploadID: 0,
          uploadError: 'There has been an error while uploading',
        });
      }
    }
  };

  useEffect(() => {
    if (uploadState.fileToUpload) {
      onFileSubmit();
    }
  }, [uploadState.fileToUpload]);

  const handleFileUpload = async (file) => {
    onFileInput(file);
  };

  if (!isShowCSVUpload) return null;

  const { theme } = offerData;

  const tabs = {
    bulkOrderTabText: theme.bulkOrderTabText,
    quickSearchTabText: theme.quickSearchTabText,
  }
  const tabsStyle = {
    tabsBackgroundColor: theme.tabsBackgroundColor,
    tabsDefaultTextColor: theme.tabsDefaultTextColor,
    tabsActiveBackgroundColor: theme.tabsActiveBackgroundColor,
    tabsActiveTextColor: theme.tabsActiveTextColor,
    tabsFontFamily: theme.tabsFontFamily,
    tabsFontSize: theme.tabsFontSize,
  };
  const triggerButton = {
    triggerButtonText: theme.triggerButtonText,
    triggerButtonFontFamily: theme.triggerButtonFontFamily,
    triggerButtonFontSize: theme.triggerButtonFontSize,
    triggerButtonBackgroundColor: theme.triggerButtonBackgroundColor,
    triggerButtonTextColor: theme.triggerButtonTextColor,
    triggerButtonStyle: theme.triggerButtonStyle,
  };
  const bannerLayout = {
    uploadBannerTitleFontFamily: theme.uploadBannerTitleFontFamily,
    uploadBannerSubtitleFontFamily: theme.uploadBannerSubtitleFontFamily,
    uploadBannerTitleFontSize: theme.uploadBannerTitleFontSize,
    uploadBannerSubtitleFontSize: theme.uploadBannerSubtitleFontSize,
    uploadBannerBackgroundColor: theme.uploadBannerBackgroundColor,
    uploadBannerTitleTextColor: theme.uploadBannerTitleTextColor,
    uploadBannerSubtitleTextColor: theme.uploadBannerSubtitleTextColor,
    uploadBannerImportTitleText: theme.uploadBannerImportTitleText,
    uploadBannerImportSubtitleText: theme.uploadBannerImportSubtitleText,
    uploadBannerCatalogTitleText: theme.uploadBannerCatalogTitleText,
    uploadBannerCatalogSubtitleText: theme.uploadBannerCatalogSubtitleText,
  }
  const title = {
    headerTitleText: theme.headerTitleText,
    headerTitleFontFamily: theme.headerTitleFontFamily,
    headerTitleFontSize: theme.headerTitleFontSize,
    headerTitleTextColor: theme.headerTitleTextColor,

    quickSearchTitleText: theme.quickSearchTitleText,

    titleText: theme.titleText,
    titleFontSize: theme.titleFontSize,
    titleFontFamily: theme.titleFontFamily,
    titleTextColor: theme.titleTextColor,
    uploadPopupHeaderBackgroundColor: theme?.uploadPopupHeaderBackgroundColor,
    uploadPopupLocationBackgroundColor: theme?.uploadPopupLocationBackgroundColor,

    uploadBannerImportHeaderTitleText: theme.uploadBannerImportHeaderTitleText,
  };
  const subtitle = {
    quickSearchSubtitleText: theme.quickSearchSubtitleText,

    subtitleText: theme.subtitleText,
    subtitleFontFamily: theme.subtitleFontFamily,
    subtitleFontSize: theme.subtitleFontSize,
    subtitleTextColor: theme.subtitleTextColor,
    watchTutorialText: theme?.watchTutorialText,
    watchTutorialTextColor: theme?.watchTutorialTextColor,
    watchTutorialFontSize: theme?.watchTutorialFontSize,
    watchTutorialFontFamily: theme?.watchTutorialFontFamily,
    watchTutorialButtonStyle: theme?.watchTutorialButtonStyle,
  };
  const uploadCSVButton = {
    uploadButtonText: theme.uploadButtonText,
    uploadButtonFontSize: theme.uploadButtonFontSize,
    uploadCSVButtonFontFamily: theme.uploadCSVButtonFontFamily,
    uploadCSVButtonBackgroundColor: theme.uploadCSVButtonBackgroundColor,
    uploadCSVButtonTextColor: theme.uploadCSVButtonTextColor,
    uploadButtonStyle: theme.uploadButtonStyle,
  };
  const downloadCatalogButton = {
    downloadButtonText: theme.downloadButtonText,
    downloadCatalogButtonFontFamily: theme.downloadCatalogButtonFontFamily,
    downloadButtonFontSize: theme.downloadButtonFontSize,
    downloadCatalogButtonBackgroundColor: theme.downloadCatalogButtonBackgroundColor,
    downloadCatalogButtonTextColor: theme.downloadCatalogButtonTextColor,
    downloadButtonStyle: theme.downloadButtonStyle,
  };
  const createOrderButton = {
    createOrderText: theme.createOrderText,
    createOrderButtonFontFamily: theme.createOrderButtonFontFamily,
    createOrderButtonFontSize: theme.createOrderButtonFontSize,
    createOrderButtonBackgroundColor: theme.createOrderButtonBackgroundColor,
    createOrderButtonTextColor: theme.createOrderButtonTextColor,
    createOrderButtonStyle: theme.createOrderButtonStyle,
  };
  const quickSearchButtonAndPlaceholder = {
    quickSearchAddButtonText: theme.quickSearchAddButtonText,
    quickSearchAddButtonBackgroundColor: theme.quickSearchAddButtonBackgroundColor,
    quickSearchAddButtonTextColor: theme.quickSearchAddButtonTextColor,
    quickSearchAddButtonFontSize: theme.quickSearchAddButtonFontSize,
    quickSearchAddButtonFontFamily: theme.quickSearchAddButtonFontFamily,
    quickSearchAddButtonStyle: theme.quickSearchAddButtonStyle,

    quickSearchPlaceholderText: theme.quickSearchPlaceholderText,
  };
  const location = {
    locationFontFamily: theme.locationFontFamily,
    locationFontSize: theme.locationFontSize,
    locationTextColor: theme.locationTextColor,
  };
  const fileName = {
    fileNameFontFamily: theme.fileNameFontFamily,
    fileNameFontSize: theme.fileNameFontSize,
    fileNameTextColor: theme.fileNameTextColor,
  };
  const orderTableHeader = {
    orderSummaryTableTitleText: theme.orderSummaryTableTitleText,
    orderTableHeaderFontFamily: theme.orderTableHeaderFontFamily,
    orderTableHeaderFontSize: theme.orderTableHeaderFontSize,
    orderTableHeaderTextColor: theme.orderTableHeaderTextColor,
    orderSummaryTableShowSummaryText: theme.orderSummaryTableShowSummaryText,
    orderSummaryTableHideSummaryText: theme.orderSummaryTableHideSummaryText,

    orderTableHeaderBackgroundColor: theme.orderTableColumnNameBackgroundColor,
  };
  const orderTableColumnName = {

    quickSearchProductColumnText: theme.quickSearchProductColumnText,
    quickSearchQuantityText: theme.quickSearchQuantityText,

    // orderSummaryTableProductText: theme.orderSummaryTableProductText,
    // orderSummaryTableQuantityText: theme.orderSummaryTableQuantityText,
    // orderSummaryTableAvailableStockText:
    //   theme.orderSummaryTableAvailableStockText,
    // orderSummaryTableMinimumQuantityText:
    //   theme.orderSummaryTableMinimumQuantityText,
    // orderSummaryTableVariantSKUText: theme.orderSummaryTableVariantSKUText,

    orderTableColumnNameFontFamily: theme.orderTableColumnNameFontFamily,
    orderTableColumnNameFontSize: theme.orderTableColumnNameFontSize,
    orderTableColumnNameTextColor: theme.orderTableColumnNameTextColor,
  };
  const orderTableContent = {
    orderTableContentFontFamily: theme.orderTableContentFontFamily,
    orderTableContentFontSize: theme.orderTableContentFontSize,
    orderTableContentTextColor: theme.orderTableContentTextColor,
  };
  const errorSummaryTitle = {
    errorTitleText: theme.errorTitleText,
    errorSummaryTitleFontFamily: theme.errorSummaryTitleFontFamily,
    errorSummaryTitleFontSize: theme.errorSummaryTitleFontSize,
    errorSummaryBackgroundColor: theme.errorSummaryBackgroundColor,
    errorCardTitleTextColor: theme.errorCardTitleTextColor,
  };
  const errorText = {
    errorInvalidVariantText: theme.errorInvalidVariantText, // this is used when the uploaded csv has data in it
    errorMinOrderQuantityText: theme.errorMinOrderQuantityText,
    errorMaxOrderQuantityText: theme.errorMaxOrderQuantityText,
    errorFileFormatText: theme.errorFileFormatText,
    errorIncrementQuantityText: theme.errorIncrementQuantityText,
    errorAvailableQuantityText: theme.errorAvailableQuantityText,
    errorMaxRowsLimitReachedText: theme.errorMaxRowsLimitReachedText,
    errorEmptyCSVText: theme.errorEmptyCSVText,
    errorIncorrectVariantText: theme.errorIncorrectVariantText, // this is used when uploaded csv is empty
    errorInvalidQuantityText: theme.errorInvalidQuantityText,
    errorOutOfStockText: theme.errorOutOfStockText,
  };
  const errorSummaryContent = {
    errorSummaryContentFontFamily: theme.errorSummaryContentFontFamily,
    errorSummaryContentFontSize: theme.errorSummaryContentFontSize,
    errorCardContentTextColor: theme.errorCardContentTextColor,
  };
  const lineItemError = {
    lineItemErrorFontFamily: theme.lineItemErrorFontFamily,
    lineItemErrorFontSize: theme.lineItemErrorFontSize,
    lineItemErrorTextColor: theme.lineItemErrorTextColor,
  };
  const otherText = {
    uploadPopupTitleText: theme.uploadPopupTitleText,
    uploadPopupDescriptionText: theme.uploadPopupDescriptionText,
    uploadPopupCancelButtonText: theme.uploadPopupCancelButtonText,
    uploadPopupSubmitButtonText: theme.uploadPopupSubmitButtonText,
    downloadCompletedText: theme.downloadCompletedText,
    fetchingCatalogueText: theme.fetchingCatalogueText,
    processingText: theme?.processingText || 'Processing',
  };

  const themeProps = {
    tabs,
    tabsStyle,
    triggerButton,
    bannerLayout,
    title,
    subtitle,
    uploadCSVButton,
    downloadCatalogButton,
    createOrderButton,
    quickSearchButtonAndPlaceholder,
    location,
    fileName,
    orderTableHeader,
    orderTableColumnName,
    orderTableContent,
    errorSummaryTitle,
    errorSummaryContent,
    lineItemError,
    errorText,
    otherText,
  };

  const data = uploadState.uploadedDataForTable ?? [];
  const tableHeadings = getColumnHeadings(data as unknown as Record<string, string>[], columns);
  const errorSummaryVar = getErrorSummaryVariable(columns);
  const columnTokens = {
    qtyToken: qtyToken,
    variantSkuToken: variantSkuToken,
    barcodeToken: barcodeToken,
    availableQtyToken: availableQtyToken,
  }


  return (
    <CSVUploadComponent
      themeProps={themeProps}
      watchTutorialLink={updatedWatchTutorialLink}
      isShowWatchTutorial={isShowWatchTutorial}
      locationOptions={locationOptions}
      locationSearchValue={locationSearchValue}
      setLocationSearchValue={setLocationSearchValue}
      selectedLocation={selectedLocation}
      setSelectedLocation={setSelectedLocation}
      onDownloadButtonClick={onDownloadButtonClick}
      downloadBtnLoading={downloadState.downloadBtnLoading}
      downloadCatalogueURL={downloadState.downloadCatalogueURL}
      progressValue={progressValue}
      tableData={currentData}
      currentPage={currentPage}
      totalPages={totalPages}
      handleNextPage={handleNextPage}
      handlePreviousPage={handlePreviousPage}
      onTableRowQuantityChange={onTableRowQuantityChange}
      onTableRowDelete={onTableRowDelete}
      onDeleteFile={onDeleteFile}
      fileName={uploadState.uploadFileName}
      isShowOrderTable={isShowOrderTable}
      setIsShowOrderTable={setIsShowOrderTable}
      createOrderUrl={createOrderState.createOrderUrl}
      showOrderURL={showDraftOrderURL}
      errorList={errorList}
      isHideDownloadButton={downloadState.isHideDownloadButton}
      createOrderLoading={createOrderState.createOrderLoading}
      onCreateOrderClick={onCreateOrderClick}
      isShowCreateOrderBtn={isShowCreateOrderBtn}
      uploadedFile={uploadState.fileToUpload}
      onFileDelete={onFileDelete}
      handleFileUpload={handleFileUpload}
      fileUploadError={uploadState.uploadError}
      isShowProgressBar={uploadState.isRequestForUploadStatus}
      onModalCancelButtonClick={onModalCancelButtonClick}
      submitButtonLoading={uploadState.submitButtonLoading}
      isShowBackBtn={false}
      isShowLocationOptions={false}
      isShowAvailableStock={isShowAvailableStock}
      isShowBarcode={isShowBarcode}
      columns={columns}
      incrementToken={incrementToken}
      minQtyToken={minQtyToken}
      maxQtyToken={maxQtyToken}
      availableQtyToken={availableQtyToken}
      columnTokens={columnTokens}
      tableHeadings={tableHeadings}
      csvUploadFormat={csvUploadFormat}
      tabsShouldBeVisible={offerData.isBulkOrderingEnabled && offerData.isQuickSearchEnabled}
      activeTab={activeTab}
      setActiveTab={setActiveTab}

      searchedValue={searchedValue}
      setSearchedValue={setSearchedValue}
      fetchSearchedProducts={fetchSearchedProducts}
      searchedProducts={searchedProducts}
      setSearchedProducts={setSearchedProducts}
      quickSearchSelectedProduct={quickSearchSelectedProduct}
      setQuickSearchSelectedProduct={setQuickSearchSelectedProduct}
      quickSearchedTableData={quickSearchedTableData}
      setQuickSearchedTableData={setQuickSearchedTableData}
      onQuickSearchProductAdd={onQuickSearchProductAdd}
      onQuickSearchProductChange={onQuickSearchProductChange}
      onClickCreateOrder={onClickCreateOrder}
      createOrderText={offerData.theme.createOrderText}
      orderUrl={orderUrl}
      createOrderButtonLoading={createOrderButtonLoading}
      setCreateOrderButtonLoading={setCreateOrderButtonLoading}
      onQuickSearchProductChangeForSearchRow={onQuickSearchProductChangeForSearchRow}
      searchedProductsLoading={searchedProductsLoading}
      getInlineError={getInlineError}
      closestMultiples={closestMultiples}
      isValueMultipleOfIncrement={isValueMultipleOfIncrement}
      env={process.env.ENV === 'stage' || process.env.ENV === 'prod' ? process.env.ENV : 'dev'}
      isAvailableStockEnabledForQuickSearch={isAvailableStockEnabledForQuickSearch}
    />
  );
};

export default CSVUpload;
